/**
 * Civilhealth Healthcare - Status Badge Component
 * High Visibility Design - WCAG AA Compliant
 *
 * Rule: Status icons/text on light backgrounds with dark colors
 * Every status has its own color system
 */

import React from 'react';
import { Chip, Box, Typography, useTheme, alpha } from '@mui/material';
import { statusColors, workflowColors, getStatusColor } from '../../theme/statusColors';

interface StatusBadgeProps {
  status: string;
  variant?: 'chip' | 'icon' | 'text';
  icon?: React.ReactNode;
  size?: 'small' | 'medium' | 'large';
  showDot?: boolean;
}

// Status to color mapping
const getStyleForStatus = (status: string) => {
  const normalizedStatus = status.toUpperCase().replace(/[_\s]/g, '');

  const mappings: Record<string, { bg: string; color: string; text: string }> = {
    // Pending states
    'PENDING': { bg: statusColors.pending.bg, color: statusColors.pending.solid, text: 'Pending' },
    'PENDINGAPPROVAL': { bg: statusColors.pending.bg, color: statusColors.pending.solid, text: 'Pending Approval' },

    // Accepted/Confirmed states
    'ACCEPTED': { bg: statusColors.accepted.bg, color: statusColors.accepted.solid, text: 'Accepted' },
    'CONFIRMED': { bg: statusColors.accepted.bg, color: statusColors.accepted.solid, text: 'Confirmed' },
    'APPROVED': { bg: statusColors.accepted.bg, color: statusColors.accepted.solid, text: 'Approved' },

    // In Progress states
    'INPROGRESS': { bg: statusColors.inProgress.bg, color: statusColors.inProgress.solid, text: 'In Progress' },
    'STARTED': { bg: statusColors.inProgress.bg, color: statusColors.inProgress.solid, text: 'Started' },

    // Completed states
    'COMPLETED': { bg: statusColors.completed.bg, color: statusColors.completed.solid, text: 'Completed' },
    'VISITED': { bg: workflowColors.visited.bg, color: workflowColors.visited.badge, text: 'Visited' },
    'FINISHED': { bg: statusColors.completed.bg, color: statusColors.completed.solid, text: 'Finished' },

    // Cancelled/Rejected states
    'CANCELLED': { bg: statusColors.cancelled.bg, color: statusColors.cancelled.solid, text: 'Cancelled' },
    'REJECTED': { bg: statusColors.cancelled.bg, color: statusColors.cancelled.solid, text: 'Rejected' },
    'NOSHOW': { bg: statusColors.cancelled.bg, color: statusColors.cancelled.solid, text: 'No Show' },

    // Other states
    'DRAFT': { bg: statusColors.neutral.bg, color: statusColors.neutral.solid, text: 'Draft' },
    'ACTIVE': { bg: statusColors.success.bg, color: statusColors.success.solid, text: 'Active' },
    'INACTIVE': { bg: statusColors.neutral.bg, color: statusColors.neutral.solid, text: 'Inactive' },

    // Priority states
    'HIGH': { bg: statusColors.error.bg, color: statusColors.error.solid, text: 'High' },
    'MEDIUM': { bg: statusColors.warning.bg, color: statusColors.warning.solid, text: 'Medium' },
    'LOW': { bg: statusColors.success.bg, color: statusColors.success.solid, text: 'Low' },

    // Error/Warning states
    'ERROR': { bg: statusColors.error.bg, color: statusColors.error.solid, text: 'Error' },
    'WARNING': { bg: statusColors.warning.bg, color: statusColors.warning.solid, text: 'Warning' },
    'SUCCESS': { bg: statusColors.success.bg, color: statusColors.success.solid, text: 'Success' },
  };

  // Try exact match first
  if (mappings[normalizedStatus]) {
    return mappings[normalizedStatus];
  }

  // Try partial match
  for (const [key, value] of Object.entries(mappings)) {
    if (normalizedStatus.includes(key) || key.includes(normalizedStatus)) {
      return value;
    }
  }

  // Default fallback
  return {
    bg: statusColors.neutral.bg,
    color: statusColors.neutral.solid,
    text: status.charAt(0).toUpperCase() + status.slice(1).toLowerCase(),
  };
};

const StatusBadge: React.FC<StatusBadgeProps> = ({
  status,
  variant = 'chip',
  icon,
  size = 'medium',
  showDot = false,
}) => {
  const theme = useTheme();
  const style = getStyleForStatus(status);

  const sizeMap = {
    small: { height: 24, fontSize: '0.75rem', padding: '0 8px', iconSize: 14 },
    medium: { height: 28, fontSize: '0.8rem', padding: '0 12px', iconSize: 16 },
    large: { height: 32, fontSize: '0.875rem', padding: '0 16px', iconSize: 18 },
  };

  const sizeConfig = sizeMap[size];

  if (variant === 'icon') {
    return (
      <Box
        sx={{
          display: 'inline-flex',
          alignItems: 'center',
          justifyContent: 'center',
          width: sizeConfig.height + 8,
          height: sizeConfig.height + 8,
          borderRadius: '50%',
          bgcolor: style.bg,
          border: `2px solid ${alpha(style.color, 0.3)}`,
          '& svg': {
            fontSize: sizeConfig.iconSize,
            color: '#FFFFFF',
          },
        }}
      >
        {icon}
      </Box>
    );
  }

  if (variant === 'text') {
    return (
      <Typography
        sx={{
          fontSize: sizeConfig.fontSize,
          fontWeight: 600,
          color: style.color,
          display: 'inline-flex',
          alignItems: 'center',
          gap: 0.5,
        }}
      >
        {showDot && (
          <Box
            sx={{
              width: 8,
              height: 8,
              borderRadius: '50%',
              bgcolor: style.color,
            }}
          />
        )}
        {style.text}
      </Typography>
    );
  }

  // Default: Chip variant
  return (
    <Chip
      label={
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
          {showDot && (
            <Box
              sx={{
                width: 6,
                height: 6,
                borderRadius: '50%',
                bgcolor: style.color,
              }}
            />
          )}
          {icon && <Box sx={{ display: 'flex', alignItems: 'center', '& svg': { fontSize: sizeConfig.iconSize } }}>{icon}</Box>}
          {style.text}
        </Box>
      }
      size={size === 'small' ? 'small' : 'medium'}
      sx={{
        height: sizeConfig.height,
        fontSize: sizeConfig.fontSize,
        fontWeight: 600,
        bgcolor: style.bg,
        color: style.color,
        border: `1px solid ${alpha(style.color, 0.3)}`,
        borderRadius: 2,
        '& .MuiChip-label': {
          padding: sizeConfig.padding,
          fontWeight: 600,
        },
      }}
    />
  );
};

export default StatusBadge;